//Mar 11 SmartListsPage.jsx:
// src/pages/SmartListsPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  List,
  ListItemButton,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  CircularProgress,
  Stack,
  Tooltip,
  Alert,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import { v4 as uuidv4 } from 'uuid';

import { useAuth } from '../contexts/AuthContext';
import { safeGetItem, safeSetItem } from '../utils/storageUtils';
import {
  listManagedFieldsForOrganization,
  listManagedOptionSetsForOrganization,
} from '../utils/managedFields';
import { saveSmartListsCanonically } from '../utils/smartListsSaveService';
import { useNavbarActions } from '../contexts/NavbarActionsContext';

import SmartListsSidePanel from '../components/SmartLists/SmartListsSidePanel';
import SmartListsWorkspaceCanvas from '../components/SmartLists/SmartListsWorkspaceCanvas';
/**
 * SmartListsPage
 * -----------------------------------------------------------------------------
 * FIRST ENTERPRISE-SAFE PAGE LAYER FOR THE NEW SMARTLISTS WORKSPACE.
 *
 * PURPOSE
 *  • Preserve the existing OrganizationFieldsPage MVP untouched
 *  • Introduce a new organization-scoped editing workspace incrementally
 *  • Reuse the existing sidepanel interaction pattern safely
 *  • Avoid coupling SmartLists to form-builder providers or form-local schema
 *  • Persist local workspace UI state with storageUtils
 *
 * CURRENT SCOPE
 *  • Reads existing org-managed fields + managed option sets from Firestore
 *  • Shows those fields in the left SmartLists sidepanel
 *  • Allows creating a NEW LOCAL draft field via the + button
 *  • Places that new draft at the FAR LEFT of the main canvas
 *  • Switches the sidebar into settings mode for the selected draft/field
 *  • Uses local workspace persistence so refreshes are resilient
 *
 * IMPORTANT
 *  • This page does NOT yet replace OrganizationFieldsPage.jsx
 *  • This page does NOT yet persist SmartLists edits back to Firestore
 *  • This page does NOT yet implement spreadsheet-grade editing
 *  • This page DOES establish the correct enterprise page boundary
 *
 * STORAGE BOUNDARY
 *  • Firestore remains the source of truth for persisted managed fields
 *  • storageUtils is used ONLY for local workspace UI/draft state
 * -----------------------------------------------------------------------------
 */

const SIDEBAR_WIDTH = '30%';

const buildWorkspaceStorageKey = (organizationId) =>
  `org_${organizationId}_smartlists_workspace`;

const buildEmptyDraft = () => {
  const localDraftId = `smartlist-draft-${uuidv4()}`;

  return {
    localDraftId,
    managedFieldId: null,
    optionsSetId: null,

    isNew: true,
    isDirty: true,
    persisted: false,

    field: {
      label: 'New SmartList Field',
      description: '',
      type: 'single-select',
      status: 'draft',

      governance: {
        allowLocalLabelOverride: false,
        allowLocalRequiredOverride: true,
        allowLocalHelperTextOverride: true,
        allowLocalPlaceholderOverride: true,
        allowLocalOptionEditing: false,
        allowLocalDependencies: false, // org-wide dependencies intentionally OFF
        allowOther: false,
      },

      displayDefaults: {
        placeholder: '',
        helperText: '',
      },
    },

    // Future-facing row model for option-grid work.
    optionRows: [],
  };
};

const mapManagedFieldToWorkspaceItem = (managedField, optionSetsById = {}) => {
  const managedFieldId = managedField?.id || uuidv4();
  const optionsSetId = managedField?.optionsSource?.optionsSetId || null;
  const optionSet = optionsSetId ? optionSetsById[optionsSetId] || null : null;

  return {
    localDraftId: `managed-${managedFieldId}`,
    managedFieldId,
    optionsSetId,

    isNew: false,
    isDirty: false,
    persisted: true,

    field: {
      label: managedField?.label || 'Managed field',
      description: managedField?.description || '',
      type: managedField?.type || 'single-select',
      status: managedField?.status || 'active',

      governance: {
        allowLocalLabelOverride:
          managedField?.governance?.allowLocalLabelOverride === true,
        allowLocalRequiredOverride:
          managedField?.governance?.allowLocalRequiredOverride === true,
        allowLocalHelperTextOverride:
          managedField?.governance?.allowLocalHelperTextOverride === true,
        allowLocalPlaceholderOverride:
          managedField?.governance?.allowLocalPlaceholderOverride === true,
        allowLocalOptionEditing:
          managedField?.governance?.allowLocalOptionEditing === true,
        allowLocalDependencies:
          managedField?.governance?.allowLocalDependencies === true,
        allowOther:
          managedField?.governance?.allowOther === true,
      },

      displayDefaults: {
        placeholder: managedField?.displayDefaults?.placeholder || '',
        helperText: managedField?.displayDefaults?.helperText || '',
      },
    },

    optionRows: Array.isArray(optionSet?.options)
      ? optionSet.options.map((opt) => ({
          rowId: `row-${opt.optionId || uuidv4()}`,
          optionId: opt.optionId || uuidv4(),
          label: opt.label || '',
          value: opt.value || '',
          hint: opt.hint || '',
          lifecycleStatus: opt.lifecycleStatus || 'active',
          liveFrom: opt.liveFrom || null,
          liveTo: opt.liveTo || null,
          isPendingRequest: false,
          requestId: null,
          dirty: false,
          isNew: false,
          isDeleted: false,
        }))
      : [],
  };
};

const getFieldTypeIcon = (fieldType) => {
  if (fieldType === 'multi-select') {
    return <CheckBoxIcon sx={{ fontSize: '0.95rem', color: '#455A64' }} />;
  }

  return <ArrowDropDownCircleIcon sx={{ fontSize: '0.95rem', color: '#455A64' }} />;
};

const getFieldTypeLabel = (fieldType) => {
  if (fieldType === 'multi-select') return 'Multi-select';
  return 'Single-select';
};

const buildWorkspaceItemsPreservingPersistedOrder = ({
  persistedWorkspaceItems = [],
  serverItems = [],
}) => {
  const safePersistedWorkspaceItems = Array.isArray(persistedWorkspaceItems)
    ? persistedWorkspaceItems
    : [];

  const safeServerItems = Array.isArray(serverItems)
    ? serverItems
    : [];

  const localOnlyDrafts = safePersistedWorkspaceItems.filter(
    (item) => item?.persisted !== true
  );

  const serverItemsByLocalDraftId = safeServerItems.reduce((acc, item) => {
    const localDraftId = item?.localDraftId || null;
    if (!localDraftId) return acc;

    acc[localDraftId] = item;
    return acc;
  }, {});

  const orderedPersistedServerItems = safePersistedWorkspaceItems
    .filter((item) => item?.persisted === true)
    .map((item) => {
      const localDraftId = item?.localDraftId || null;
      if (!localDraftId) return null;

      return serverItemsByLocalDraftId[localDraftId] || null;
    })
    .filter(Boolean);

  const orderedServerIds = new Set(
    orderedPersistedServerItems
      .map((item) => item?.localDraftId || null)
      .filter(Boolean)
  );

  const unorderedServerItems = safeServerItems.filter((item) => {
    const localDraftId = item?.localDraftId || null;
    return localDraftId ? !orderedServerIds.has(localDraftId) : true;
  });

  return [
    ...localOnlyDrafts,
    ...orderedPersistedServerItems,
    ...unorderedServerItems,
  ];
};

const SmartListsPage = () => {
  const { currentUser, organizationId } = useAuth() || {};
  const { setNavbarActions, resetNavbarActions } = useNavbarActions();

  const [managedFields, setManagedFields] = useState([]);
  const [managedOptionSetsById, setManagedOptionSetsById] = useState({});

  const [workspaceItems, setWorkspaceItems] = useState([]);
  const [selectedDraftId, setSelectedDraftId] = useState(null);
  const [sidebarMode, setSidebarMode] = useState('list'); // 'list' | 'settings'

  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState(null);

  const canvasRef = useRef(null);

  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [saveMessage, setSaveMessage] = useState('');

  const hasUnsavedChanges = useMemo(() => {
    return (workspaceItems || []).some(
      (item) => item?.isDirty === true || item?.isNew === true
    );
  }, [workspaceItems]);

  const workspaceStorageKey = useMemo(() => {
    if (!organizationId) return null;
    return buildWorkspaceStorageKey(organizationId);
  }, [organizationId]);

  const selectedItem = useMemo(() => {
    return workspaceItems.find((item) => item.localDraftId === selectedDraftId) || null;
  }, [workspaceItems, selectedDraftId]);

  const visibleColumnGroups = useMemo(() => {
    return workspaceItems;
  }, [workspaceItems]);

  const persistWorkspaceState = useCallback((nextState) => {
    if (!workspaceStorageKey) return;
    safeSetItem(workspaceStorageKey, nextState);
  }, [workspaceStorageKey]);

  const hydrateWorkspaceState = useCallback(() => {
    if (!workspaceStorageKey) return null;
    return safeGetItem(workspaceStorageKey);
  }, [workspaceStorageKey]);

  const refreshManagedRegistry = useCallback(async () => {
    if (!organizationId) {
      setManagedFields([]);
      setManagedOptionSetsById({});
      setWorkspaceItems([]);
      return;
    }

    setPageLoading(true);
    setPageError(null);

    try {
      const [fieldsResponse, optionSetsResponse] = await Promise.all([
        listManagedFieldsForOrganization({
          organizationId,
          includeArchived: true,
        }),
        listManagedOptionSetsForOrganization({
          organizationId,
          includeArchived: true,
        }),
      ]);

      const fields = Array.isArray(fieldsResponse?.data) ? fieldsResponse.data : [];
      const optionSets = Array.isArray(optionSetsResponse?.data) ? optionSetsResponse.data : [];

      const nextOptionSetsById = optionSets.reduce((acc, setDoc) => {
        acc[setDoc.id] = setDoc;
        return acc;
      }, {});

      setManagedFields(fields);
      setManagedOptionSetsById(nextOptionSetsById);

const persistedWorkspace = hydrateWorkspaceState();

const serverItems = fields.map((field) =>
  mapManagedFieldToWorkspaceItem(field, nextOptionSetsById)
);

// Use buildWorkspaceItemsPreservingPersistedOrder to respect persisted order
const nextWorkspaceItems = buildWorkspaceItemsPreservingPersistedOrder({
  persistedWorkspaceItems: persistedWorkspace?.workspaceItems || [],
  serverItems,
});

setWorkspaceItems(nextWorkspaceItems);

// Restore selected draft if present
if (persistedWorkspace?.selectedDraftId) {
  setSelectedDraftId(persistedWorkspace.selectedDraftId);
}

// Restore sidebar mode if present
if (persistedWorkspace?.sidebarMode) {
  setSidebarMode(persistedWorkspace.sidebarMode);
}

// Persist the merged workspace state
persistWorkspaceState({
  workspaceItems: nextWorkspaceItems,
  selectedDraftId: persistedWorkspace?.selectedDraftId || null,
  sidebarMode: persistedWorkspace?.sidebarMode || 'list',
});

console.log('[SmartListsPage] Managed registry loaded successfully with persisted order:', {
  organizationId,
  managedFieldCount: fields.length,
  managedOptionSetCount: Object.keys(nextOptionSetsById).length,
  workspaceItemsOrder: nextWorkspaceItems.map((item) => item.localDraftId),
});
    } catch (error) {
      console.error('[SmartListsPage] Error loading managed registry:', error);
      setPageError(error?.message || 'Failed to load SmartLists data.');
    } finally {
      setPageLoading(false);
    }
  }, [organizationId, hydrateWorkspaceState, persistWorkspaceState]);


const applySavedSmartListsState = useCallback(({
  latestWorkspaceSnapshot = [],
  saveData = null,
}) => {
  const optionSetResults = Array.isArray(saveData?.optionSetResults)
    ? saveData.optionSetResults
    : [];

  const fieldResults = Array.isArray(saveData?.fieldResults)
    ? saveData.fieldResults
    : [];

  const optionSetIdByLocalDraftId = optionSetResults.reduce((acc, result) => {
    const localDraftId = result?.localDraftId || null;
    const optionsSetId = result?.optionsSetId || null;
    if (localDraftId && optionsSetId) {
      acc[localDraftId] = optionsSetId;
    }
    return acc;
  }, {});

  const managedFieldIdByLocalDraftId = fieldResults.reduce((acc, result) => {
    const localDraftId = result?.localDraftId || null;
    const managedFieldId = result?.managedFieldId || null;
    if (localDraftId && managedFieldId) {
      acc[localDraftId] = managedFieldId;
    }
    return acc;
  }, {});

  const nextWorkspaceItems = (Array.isArray(latestWorkspaceSnapshot) ? latestWorkspaceSnapshot : []).map((group) => {
    const localDraftId = group?.localDraftId || null;

    return {
      ...group,
      managedFieldId:
        managedFieldIdByLocalDraftId[localDraftId] ||
        group?.managedFieldId ||
        null,
      optionsSetId:
        optionSetIdByLocalDraftId[localDraftId] ||
        group?.optionsSetId ||
        null,
      isDirty: false,
      isNew: false,
      persisted: true,
      optionRows: Array.isArray(group?.optionRows)
        ? group.optionRows.map((row) => ({
            ...row,
            dirty: false,
            isNew: false,
          }))
        : [],
    };
  });

  setWorkspaceItems(nextWorkspaceItems);

  setManagedOptionSetsById((prev) => {
    const next = { ...(prev || {}) };

    optionSetResults.forEach((result) => {
      const optionsSetId = result?.optionsSetId || null;
      const data = result?.data || null;
      if (!optionsSetId || !data) return;

      next[optionsSetId] = {
        ...(next[optionsSetId] || {}),
        ...data,
        id: optionsSetId,
      };
    });

    return next;
  });

  setManagedFields((prev) => {
    const byId = new Map(
      (Array.isArray(prev) ? prev : []).map((field) => [field?.id, field])
    );

    fieldResults.forEach((result) => {
      const managedFieldId = result?.managedFieldId || null;
      const data = result?.data || null;
      if (!managedFieldId || !data) return;

      byId.set(managedFieldId, {
        ...(byId.get(managedFieldId) || {}),
        ...data,
        id: managedFieldId,
      });
    });

    return Array.from(byId.values());
  });

  console.log('[SmartListsPage] Applied saved SmartLists state locally without full registry refresh.', {
    workspaceGroupCount: nextWorkspaceItems.length,
    optionSetResultCount: optionSetResults.length,
    fieldResultCount: fieldResults.length,
  });
}, []);

  useEffect(() => {
    if (!currentUser || !organizationId) return;
    refreshManagedRegistry();
  }, [currentUser, organizationId, refreshManagedRegistry]);

  useEffect(() => {
    if (!workspaceStorageKey) return;

    persistWorkspaceState({
      workspaceItems,
      selectedDraftId,
      sidebarMode,
    });
  }, [workspaceItems, selectedDraftId, sidebarMode, workspaceStorageKey, persistWorkspaceState]);


  const handleCreateNewField = useCallback(() => {
    const draft = buildEmptyDraft();

    setWorkspaceItems((prev) => [draft, ...(prev || [])]);
    setSelectedDraftId(draft.localDraftId);
    setSidebarMode('settings');

    console.log('[SmartListsPage] Created local SmartList draft:', {
      localDraftId: draft.localDraftId,
      type: draft.field.type,
      label: draft.field.label,
    });
  }, []);

  const handleSelectField = useCallback((localDraftId) => {
    setSelectedDraftId(localDraftId);
    setSidebarMode('settings');

    console.log('[SmartListsPage] Selected SmartList field for editing:', {
      localDraftId,
    });
  }, []);



  const handleDone = useCallback(() => {
    // FIRST PASS:
    // "Done" exits settings mode and returns to the field list.
    // We intentionally do NOT persist SmartLists changes to Firestore yet.
    setSidebarMode('list');
    setSelectedDraftId(null);

    console.log('[SmartListsPage] Done clicked. Returning to list mode without persistence yet.');
  }, []);

  const handleRowReorder = useCallback(({
    sectionIndex,
    sourceIndex,
    destinationIndex,
  }) => {
    if (
      !Number.isInteger(sourceIndex) ||
      !Number.isInteger(destinationIndex) ||
      sourceIndex === destinationIndex
    ) {
      return;
    }

    setWorkspaceItems((prev) => {
      const currentItems = Array.isArray(prev) ? [...prev] : [];

      if (
        sourceIndex < 0 ||
        destinationIndex < 0 ||
        sourceIndex >= currentItems.length ||
        destinationIndex >= currentItems.length
      ) {
        console.warn('[SmartListsPage] Ignoring SmartLists reorder because the source/destination index is out of bounds.', {
          sectionIndex,
          sourceIndex,
          destinationIndex,
          workspaceItemCount: currentItems.length,
        });
        return prev;
      }

      const [movedItem] = currentItems.splice(sourceIndex, 1);
      currentItems.splice(destinationIndex, 0, movedItem);

      const dirtyStart = Math.min(sourceIndex, destinationIndex);
      const dirtyEnd = Math.max(sourceIndex, destinationIndex);

      const nextWorkspaceItems = currentItems.map((item, index) => {
        if (index < dirtyStart || index > dirtyEnd) return item;

        return {
          ...item,
          isDirty: true,
          order: index, // persist sequential order for each group
        };
      });

      console.log('[SmartListsPage] Reordered SmartLists sidebar items.', {
        sectionIndex,
        sourceIndex,
        destinationIndex,
        movedLocalDraftId: movedItem?.localDraftId || null,
        nextOrderedDraftIds: nextWorkspaceItems.map((item) => item?.localDraftId || null),
      });

      return nextWorkspaceItems;
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!currentUser?.uid || !organizationId) {
      console.error('[SmartListsPage] Cannot save SmartLists without current user or organizationId.', {
        hasCurrentUser: !!currentUser?.uid,
        organizationId,
      });
      setSaveStatus('error');
      setSaveMessage('Missing current user or organization context.');
      return;
    }

    if (!canvasRef.current || typeof canvasRef.current.getWorkspaceSnapshot !== 'function') {
      console.error('[SmartListsPage] SmartLists canvas ref is unavailable or missing getWorkspaceSnapshot().');
      setSaveStatus('error');
      setSaveMessage('Unable to access the latest SmartLists grid state.');
      return;
    }

    try {
      setSaveStatus('saving');
      setSaveMessage('');

      const latestWorkspaceSnapshot = canvasRef.current.getWorkspaceSnapshot();

      console.log('[SmartListsPage] handleSave pulled latest workspace snapshot from canvas:', {
        groupCount: Array.isArray(latestWorkspaceSnapshot) ? latestWorkspaceSnapshot.length : 0,
        groupIds: Array.isArray(latestWorkspaceSnapshot)
          ? latestWorkspaceSnapshot.map((group) => group?.localDraftId || null)
          : [],
      });

      const dirtyGroupIds = new Set(
        (Array.isArray(latestWorkspaceSnapshot) ? latestWorkspaceSnapshot : [])
          .filter((group) => group?.isDirty === true || group?.isNew === true)
          .map((group) => group?.localDraftId)
          .filter(Boolean)
      );

      console.log('[SmartListsPage] handleSave computed dirty SmartLists groups:', {
        dirtyGroupCount: dirtyGroupIds.size,
        dirtyGroupIds: Array.from(dirtyGroupIds),
      });

      const saveResponse = await saveSmartListsCanonically({
        organizationId,
        userId: currentUser.uid,
        workspaceItems: latestWorkspaceSnapshot,
        dirtyGroupIds,
        managedFields,
        managedOptionSets: Object.values(managedOptionSetsById || {}),
      });

      if (saveResponse?.status !== 'success') {
        throw new Error(saveResponse?.message || 'Failed to save SmartLists.');
      }

      console.log('[SmartListsPage] SmartLists saved successfully.', {
        organizationId,
        diagnostics: saveResponse?.data?.diagnostics || null,
        optionSetWriteCount: Array.isArray(saveResponse?.data?.optionSetResults)
          ? saveResponse.data.optionSetResults.length
          : 0,
        fieldWriteCount: Array.isArray(saveResponse?.data?.fieldResults)
          ? saveResponse.data.fieldResults.length
          : 0,
      });

      applySavedSmartListsState({
        latestWorkspaceSnapshot,
        saveData: saveResponse?.data || null,
      });

      setSaveStatus('saved');
      setSaveMessage('SmartLists saved successfully.');
    } catch (error) {
      console.error('[SmartListsPage] SmartLists save failed:', error);
      setSaveStatus('error');
      setSaveMessage(error?.message || 'Failed to save SmartLists.');
    }
  }, [
    currentUser,
    organizationId,
    managedFields,
    managedOptionSetsById,
    applySavedSmartListsState,
  ]);

  useEffect(() => {
    setNavbarActions({
      pageKey: 'smartlists',
      primaryActionLabel: saveStatus === 'saving' ? 'Saving...' : 'Save',
      primaryActionEnabled: hasUnsavedChanges && saveStatus !== 'saving',
      primaryActionLoading: saveStatus === 'saving',
      primaryActionOnClick: handleSave,
      dirty: hasUnsavedChanges,
      statusText: saveMessage || '',
    });

    return () => {
      resetNavbarActions();
    };
  }, [
    hasUnsavedChanges,
    handleSave,
    resetNavbarActions,
    saveMessage,
    saveStatus,
    setNavbarActions,
  ]);
  
  const handleMarkGroupDirty = useCallback((localDraftId) => {
    if (!localDraftId) return;

    setWorkspaceItems((prev) =>
      (prev || []).map((item) => {
        if (item.localDraftId !== localDraftId) return item;
        if (item.isDirty === true) return item;

        console.log('[SmartListsPage] Marking SmartList group dirty from canvas activity:', {
          localDraftId,
        });

        return {
          ...item,
          isDirty: true,
        };
      })
    );
  }, []);

  const handleFieldDraftChange = useCallback((patch) => {
    if (!selectedDraftId) return;

    setWorkspaceItems((prev) =>
      (prev || []).map((item) => {
        if (item.localDraftId !== selectedDraftId) return item;

        return {
          ...item,
          isDirty: true,
          field: {
            ...item.field,
            ...patch,
            governance: {
              ...(item.field?.governance || {}),
              ...(patch?.governance || {}),
            },
            displayDefaults: {
              ...(item.field?.displayDefaults || {}),
              ...(patch?.displayDefaults || {}),
            },
          },
        };
      })
    );
  }, [selectedDraftId]);

  const renderListTopBar = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '50px',
        px: 2,
        py: 1,
        backgroundColor: 'rgba(213, 228, 249, 0.5)',
        borderBottom: '1px solid #e0e0e0',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#003459' }}
        >
          SmartLists
        </Typography>

        {hasUnsavedChanges ? (
          <Box
            sx={{
              ml: 1,
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#d97706',
              flexShrink: 0,
            }}
          />
        ) : null}
      </Box>

      <Tooltip title="Create a new organization-managed field" arrow>
        <IconButton
          onClick={handleCreateNewField}
          sx={{
            color: '#003459',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <AddIcon sx={{ fontSize: '1rem' }} />
        </IconButton>
      </Tooltip>
    </Box>
  );

    const renderSettingsTopBar = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '50px',
        px: 1,
        py: 1,
        backgroundColor: 'rgba(213, 228, 249, 0.5)',
        borderBottom: '1px solid #e0e0e0',
      }}
    >
      <IconButton
        onClick={handleDone}
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: '#003459',
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <ArrowBackIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
          Done
        </Typography>
      </IconButton>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {hasUnsavedChanges ? (
          <Box
            sx={{
              mr: 1,
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#d97706',
              flexShrink: 0,
            }}
          />
        ) : null}
      </Box>
    </Box>
  );

  const listSections = useMemo(() => {
    return [
      {
        title: 'Organization SmartLists',
        items:
          workspaceItems.length > 0
            ? workspaceItems.map((item) => {
                return {
                  localDraftId: item.localDraftId,
                  element: (
                    <List disablePadding>
                      <ListItemButton
                        onClick={() => handleSelectField(item.localDraftId)}
                        selected={selectedDraftId === item.localDraftId}
                        sx={{
                          minHeight: '36px',
                          px: 1,
                          py: 0.5,
                          borderRadius: '4px',
                          backgroundColor: '#F4F6F8',
                          border: '1px solid #CED4DA',
                          width: 'calc(100% - 25px)',
                          marginLeft: '15px',
                          '&:hover': {
                            border: '1px solid #1976d2',
                            backgroundColor: '#F4F6F8',
                          },
                          '&.Mui-selected': {
                            backgroundColor: '#F4F6F8',
                            border: '1px solid #1976d2',
                            '&:hover': {
                              backgroundColor: '#F4F6F8',
                            },
                          },
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                          {getFieldTypeIcon(item?.field?.type)}
                          <Typography
                            variant="body2"
                            sx={{
                              flexGrow: 1,
                              fontSize: '0.85rem',
                              color: '#455A64',
                              fontWeight: 'normal',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item?.field?.label || 'Untitled field'}
                          </Typography>
                        </Stack>
                      </ListItemButton>
                    </List>
                  ),
                };
              })
            : [
                {
                  element: (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      No SmartLists found yet. Click the + button to create your first organization-managed field.
                    </Alert>
                  ),
                },
              ],
      },
    ];
  }, [workspaceItems, selectedDraftId, handleSelectField]);

  const settingsContent = useMemo(() => {
    if (!selectedItem) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Select a SmartList field to edit.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2, overflowY: 'auto' }}>
        {saveStatus === 'saved' && saveMessage ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {saveMessage}
          </Alert>
        ) : null}

        {saveStatus === 'error' && saveMessage ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveMessage}
          </Alert>
        ) : null}

        <Typography variant="body2" sx={{ mb: 1.5, mt: 1 }}>
          <span style={{ fontWeight: 'bold', color: '#003459' }}>Field Type:</span>
          <span style={{ color: '#6b7280', marginLeft: '8px' }}>
            {getFieldTypeLabel(selectedItem?.field?.type)}
          </span>
        </Typography>

        <FormControl fullWidth margin="normal">
          <InputLabel>Field Type</InputLabel>
          <Select
            label="Field Type"
            value={selectedItem?.field?.type || 'single-select'}
            onChange={(e) => handleFieldDraftChange({ type: e.target.value })}
          >
            <MenuItem value="single-select">Single-select</MenuItem>
            <MenuItem value="multi-select">Multi-select</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Field Label"
          margin="normal"
          value={selectedItem?.field?.label || ''}
          onChange={(e) => handleFieldDraftChange({ label: e.target.value })}
        />

        <TextField
          fullWidth
          label="Description"
          margin="normal"
          value={selectedItem?.field?.description || ''}
          onChange={(e) => handleFieldDraftChange({ description: e.target.value })}
        />

        <TextField
          fullWidth
          label="Default Placeholder"
          margin="normal"
          value={selectedItem?.field?.displayDefaults?.placeholder || ''}
          onChange={(e) =>
            handleFieldDraftChange({
              displayDefaults: {
                placeholder: e.target.value,
              },
            })
          }
        />

        <TextField
          fullWidth
          label="Default Helper Text"
          margin="normal"
          value={selectedItem?.field?.displayDefaults?.helperText || ''}
          onChange={(e) =>
            handleFieldDraftChange({
              displayDefaults: {
                helperText: e.target.value,
              },
            })
          }
        />

        <FormControlLabel
          control={
            <Switch
              checked={selectedItem?.field?.governance?.allowOther === true}
              onChange={(e) =>
                handleFieldDraftChange({
                  governance: {
                    allowOther: e.target.checked,
                  },
                })
              }
              color="primary"
              size="small"
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 'light', color: 'text.secondary' }}>
              Allow "Other"
            </Typography>
          }
          sx={{ ml: 0.5, mt: 1, mb: 1 }}
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#003459', mb: 1 }}>
          Governance Snapshot
        </Typography>

        <Stack spacing={1}>
          <FormControlLabel
            control={
              <Switch
                checked={selectedItem?.field?.governance?.allowLocalRequiredOverride === true}
                onChange={(e) =>
                  handleFieldDraftChange({
                    governance: {
                      allowLocalRequiredOverride: e.target.checked,
                    },
                  })
                }
                color="primary"
                size="small"
              />
            }
            label="Allow local required override"
          />

          <FormControlLabel
            control={
              <Switch
                checked={selectedItem?.field?.governance?.allowLocalHelperTextOverride === true}
                onChange={(e) =>
                  handleFieldDraftChange({
                    governance: {
                      allowLocalHelperTextOverride: e.target.checked,
                    },
                  })
                }
                color="primary"
                size="small"
              />
            }
            label="Allow local helper text override"
          />

          <FormControlLabel
            control={
              <Switch
                checked={selectedItem?.field?.governance?.allowLocalPlaceholderOverride === true}
                onChange={(e) =>
                  handleFieldDraftChange({
                    governance: {
                      allowLocalPlaceholderOverride: e.target.checked,
                    },
                  })
                }
                color="primary"
                size="small"
              />
            }
            label="Allow local placeholder override"
          />
        </Stack>


      </Box>
    );
  }, [selectedItem, handleFieldDraftChange, saveMessage, saveStatus]);

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 140px)',
        minHeight: '640px',
        border: '1px solid #e0e0e0',
        backgroundColor: '#f8fafc',
        overflow: 'hidden',
      }}
    >
      {/* Sidebar */}
      <SmartListsSidePanel
        mode={sidebarMode}
        sections={listSections}
        settingsContent={settingsContent}
        topBar={sidebarMode === 'settings' ? renderSettingsTopBar() : renderListTopBar()}
        width={SIDEBAR_WIDTH}
        minWidth={SIDEBAR_WIDTH}
        maxWidth={SIDEBAR_WIDTH}
        storageKeyPrefix="smartlists_sidepanel_expanded"
        collapsedDirtyBadgeVisible={hasUnsavedChanges}
        onCreateNewField={handleCreateNewField}
        onRowReorder={handleRowReorder}
      />

      {/* Main canvas */}
      <SmartListsWorkspaceCanvas
        ref={canvasRef}
        pageLoading={pageLoading}
        pageError={pageError}
        visibleColumnGroups={visibleColumnGroups}
        selectedDraftId={selectedDraftId}
        onSelectField={handleSelectField}
        onMarkGroupDirty={handleMarkGroupDirty}
      />
    </Box>
  );
};

export default SmartListsPage;